import { ILog } from "@ihr-radioedit/inferno-core";
import { PwsShow, getSdk, SDKInterface } from "@ihr-radioedit/inferno-webapi";
import { GraphQLRequestFactory } from "@ihr-radioedit/gql-client";
import axios from "axios";
import { siteSlugToShow } from "./util";

export type CallableSdk = {
  [K in keyof SDKInterface]: SDKInterface[K] extends {
    queryAsPromise(vars?: infer V): Promise<{ data?: infer TData }>;
  }
    ? (variables?: V, headers?: Record<string, string>) => Promise<TData>
    : never;
};

export abstract class PWSGraphQLServiceBase {
  protected show: PwsShow;
  protected sdk: CallableSdk;
  protected appToken: string | undefined;
  protected log: ReturnType<typeof ILog.logger>;
  protected protocol: string;
  protected pwsHost: string;

  constructor(host: string, siteSlug: string, appToken: string | undefined, logName: string, pwsHost?: string) {
    this.log = ILog.logger(logName);
    this.show = siteSlugToShow(siteSlug);
    this.appToken = appToken;

    this.protocol = host.includes("localhost") ? "http" : "https";
    const endpoint = `${this.protocol}://${host.replace(/\/+$/, "")}/graphql`;
    this.pwsHost = pwsHost || host.replace(/webapi\./g, "pws.");

    const factory = <V, R>(name: string | undefined, _type: string, query: string) => {
      return async (variables?: V, headers?: Record<string, string>): Promise<R> => {
        const response = await axios.post(
          endpoint,
          {
            query,
            variables,
          },
          {
            headers: {
              "Content-Type": "application/json",
              ...(this.appToken && { Authorization: `Bearer ${this.appToken}` }),
              ...headers,
            },
          },
        );

        if (response.data.errors) {
          this.log.error(`GraphQL Errors in ${name}:`, response.data.errors);
          throw new Error(JSON.stringify(response.data.errors));
        }
        return response.data.data;
      };
    };

    this.sdk = getSdk(factory as unknown as GraphQLRequestFactory) as unknown as CallableSdk;
  }

  protected getAuthHeaders(accessToken?: string | null) {
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  }

  protected getCommonHeaders(accessToken?: string | null) {
    return {
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      "Content-Type": "application/json",
    };
  }

  protected async withLogging<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      this.log.error(`Error in ${operation}:`, e);
      throw e;
    }
  }
}
